// NotesPanel - Floating facilitator notes panel
// Implements US-2.5: Facilitator Notes Panel

import React, { useState } from 'react';
import { FileText, X, Pencil, Trash2 } from 'lucide-react';
import { Card as CardType } from '../types/card';
import { CardForm } from './CardForm';
import styles from './NotesPanel.module.css';

interface NotesPanelProps {
  notes: CardType[];
  sessionId: string;
  isFacilitator: boolean;
  onCreateNote: (text: string) => void;
  onUpdateNote: (noteId: string, text: string) => void;
  onDeleteNote: (noteId: string) => void;
}

export const NotesPanel: React.FC<NotesPanelProps> = ({
  notes,
  sessionId,
  isFacilitator,
  onCreateNote,
  onUpdateNote,
  onDeleteNote,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);

  // HANDLE create note
  const handleCreateNote = (text: string) => {
    onCreateNote(text);
    setIsAdding(false);
  };

  // HANDLE cancel add
  const handleCancelAdd = () => {
    setIsAdding(false);
  };

  // HANDLE start edit
  const handleStartEdit = (noteId: string) => {
    setEditingNoteId(noteId);
  };

  // HANDLE update note
  const handleUpdateNote = (text: string) => {
    if (editingNoteId) {
      onUpdateNote(editingNoteId, text);
      setEditingNoteId(null);
    }
  };

  // HANDLE cancel edit
  const handleCancelEdit = () => {
    setEditingNoteId(null);
  };

  // HANDLE delete note
  const handleDeleteNote = (noteId: string) => {
    if (window.confirm('Diese Notiz löschen?')) {
      onDeleteNote(noteId);
    }
  };

  // HANDLE toggle panel
  const togglePanel = () => {
    setIsOpen(!isOpen);
  };

  // FORMAT timestamp for display
  const formatTimestamp = (date: Date) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <>
      {/* FLOATING BUTTON */}
      <button
        className={styles.notesButton}
        onClick={togglePanel}
        title="Facilitator-Notizen"
      >
        <FileText size={16} /> Notizen {notes.length > 0 && `(${notes.length})`}
      </button>

      {/* OVERLAY PANEL */}
      {isOpen && (
        <>
          {/* BACKDROP - click to close */}
          <div className={styles.notesBackdrop} onClick={togglePanel} />

          {/* PANEL CONTENT */}
          <div className={styles.notesPanel}>
            <div className={styles.notesPanelHeader}>
              <h3 className={styles.notesPanelTitle}><FileText size={20} /> Facilitator-Notizen</h3>
              <button
                className={styles.notesPanelClose}
                onClick={togglePanel}
                title="Schließen"
              >
                <X size={20} />
              </button>
            </div>

            <div className={styles.notesPanelInfo}>
              {isFacilitator ? (
                <p className={styles.notesInfoFacilitator}>
                  Halte Parking-Lot-Items, Beobachtungen und Kontext fest. Alle Teilnehmer können diese Notizen sehen.
                </p>
              ) : (
                <p className={styles.notesInfoParticipant}>
                  Diese Notizen werden vom Facilitator gepflegt und sind für alle Teilnehmer sichtbar.
                </p>
              )}
            </div>

            <div className={styles.notesPanelContent}>
              {/* ADD NOTE BUTTON (facilitator only) */}
              {isFacilitator && !isAdding && (
                <button
                  className={styles.btnAddNote}
                  onClick={() => setIsAdding(true)}
                >
                  + Notiz hinzufügen
                </button>
              )}

              {/* ADD NOTE FORM */}
              {isAdding && (
                <div className={styles.addNoteForm}>
                  <CardForm
                    onSubmit={handleCreateNote}
                    onCancel={handleCancelAdd}
                    submitLabel="Notiz hinzufügen"
                  />
                </div>
              )}

              {/* NOTES LIST */}
              <div className={styles.notesList}>
                {notes.map((note) => (
                  <div key={note.id} className={styles.noteItem}>
                    {editingNoteId === note.id ? (
                      // EDIT MODE
                      <div className={styles.noteEditForm}>
                        <CardForm
                          onSubmit={handleUpdateNote}
                          onCancel={handleCancelEdit}
                          submitLabel="Speichern"
                          initialText={note.text}
                        />
                      </div>
                    ) : (
                      // VIEW MODE
                      <>
                        <div className={styles.noteContent}>
                          <p className={styles.noteText}>{note.text}</p>
                          <div className={styles.noteMeta}>
                            <span className={styles.noteTimestamp}>
                              {formatTimestamp(note.createdAt)}
                            </span>
                            {note.updatedAt !== note.createdAt && (
                              <span className={styles.noteEdited}>(bearbeitet)</span>
                            )}
                          </div>
                        </div>
                        {isFacilitator && (
                          <div className={styles.noteActions}>
                            <button
                              className={styles.btnNoteEdit}
                              onClick={() => handleStartEdit(note.id)}
                              title="Notiz bearbeiten"
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              className={styles.btnNoteDelete}
                              onClick={() => handleDeleteNote(note.id)}
                              title="Notiz löschen"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>

              {/* EMPTY STATE */}
              {notes.length === 0 && !isAdding && (
                <div className={styles.notesEmptyState}>
                  <p>Noch keine Notizen</p>
                  {!isFacilitator && (
                    <p className={styles.notesEmptyHint}>
                      Der Facilitator kann während der Retrospektive Notizen hinzufügen.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};
